import { fetchData } from '@databases/database-state';
import { KnownUser, SearchResult } from '@databases/types/confluence';

import { nextPagination } from '../pagination';

import { retry } from './fetch';
import { quoteCql, rejectOnError } from './utils';

async function* searchUsers(
  cql: string,
  expand: string[] = [],
  limit = Infinity
): AsyncGenerator<KnownUser, void, void> {
  const results = nextPagination<SearchResult<KnownUser>>(
    async (url) => {
      return await retry(
        async () => {
          const res = await fetchData(
            url ??
              `/rest/api/search/user?${new URLSearchParams({
                cql: `type=user${cql ? ` and ${cql}` : ''}`,
                expand: expand.join(','),
                limit: `${Math.min(200, limit)}`,
              })}`
          ).then(rejectOnError);
          return await res.json();
        },
        (err) => err._orderlyStatus === 500 && err.message.includes('.ServiceException:')
      );
    },
    { limit }
  );

  try {
    for await (const result of results) {
      yield result.user;
    }
  } catch (err: any) {
    if (
      err._orderlyStatus === 403 &&
      err.message.includes('User not permitted to view user profiles')
    ) {
      // User is not allowed to view user profiles. This can happen for example when an anoymous user is browsing a database.
      // We ignore the error and simply yield nothing, to be consistent with users not being allowed to view certain content.
    } else {
      throw err;
    }
  }
}

export function getUsersByIds(
  accountIds: string[],
  expand: string[] = []
): AsyncGenerator<KnownUser, void, void> {
  if (accountIds.length == 0) {
    return (async function* () {})();
  }

  const cql = `user in (${accountIds.map((id) => quoteCql(id)).join(',')})`;
  return searchUsers(cql, expand);
}
